import { useState } from "react";

interface FilterPillProps<T extends string> {
  value: T;
  options: Array<{ id: T; label: string }>;
  onChange: (id: T) => void;
}

function FilterPill<T extends string>({
  value,
  options,
  onChange,
}: FilterPillProps<T>): JSX.Element {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-baseline gap-1.5 rounded-md border border-cyber-400/40 bg-cyber-400/5 px-2.5 py-0.5 text-cyber-400 transition hover:border-cyber-400/70 hover:bg-cyber-400/10"
      >
        <span className="text-glow-cyber font-medium">{current?.label ?? value}</span>
        <span aria-hidden className="text-[10px] text-cyber-400/70">▾</span>
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10"
          />
          <ul className="absolute left-0 top-full z-20 mt-1 min-w-[200px] rounded-md border border-ink-400 bg-ink-700 py-1 shadow-glow-cyber">
            {options.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                  }}
                  className={[
                    "block w-full px-3 py-1.5 text-left text-sm",
                    o.id === value
                      ? "bg-cyber-400/15 text-cyber-400"
                      : "text-chrome-200 hover:bg-ink-600 hover:text-chrome-50",
                  ].join(" ")}
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </span>
  );
}

interface NarrativeBlockProps {
  scopeOptions: string[];
  windowOptions: Array<{ id: string; label: string }>;
  lensOptions: Array<{ id: string; label: string }>;
  totals: {
    scenarios_run: number;
    devices_in_scope: number;
    high_count: number;
    medium_count: number;
    low_count: number;
    caught_count: number;
    score_letter: string;
    score_value: number;
    trend_direction: "up" | "down" | "flat";
    trend_delta: number;
  };
}

const NUMBER = "text-cyber-400 text-glow-cyber font-semibold tabular-nums";

export function NarrativeBlock({
  scopeOptions,
  windowOptions,
  lensOptions,
  totals,
}: NarrativeBlockProps): JSX.Element {
  const [scope, setScope] = useState<string>(scopeOptions[0] ?? "this household");
  const [windowId, setWindowId] = useState<string>(windowOptions[0]?.id ?? "current");
  const [lensId, setLensId] = useState<string>(lensOptions[0]?.id ?? "all");

  const scopeFlat = scopeOptions.map((s) => ({ id: s, label: s }));

  const trendArrow =
    totals.trend_direction === "up"
      ? "▲"
      : totals.trend_direction === "down"
        ? "▼"
        : "▬";
  const trendColor =
    totals.trend_direction === "up"
      ? "text-ok-400"
      : totals.trend_direction === "down"
        ? "text-crit-400"
        : "text-chrome-300";

  return (
    <article className="panel relative p-8">
      <div className="absolute -top-px left-6 h-0.5 w-16 bg-cyber-400/70 shadow-[0_0_12px_#22d3ee]" />
      <div className="label-overline mb-4">posture summary · plain language</div>
      <p className="max-w-3xl text-lg leading-[1.85] text-chrome-100">
        Across{" "}
        <FilterPill value={scope} options={scopeFlat} onChange={setScope} />, during{" "}
        <FilterPill value={windowId} options={windowOptions} onChange={setWindowId} />, we
        ran{" "}
        <span className={NUMBER}>{totals.scenarios_run}</span> attack scenarios against{" "}
        <span className={NUMBER}>{totals.devices_in_scope}</span> devices and caught{" "}
        <span className="text-ok-400 font-semibold tabular-nums">
          {totals.caught_count}
        </span>{" "}
        of them in flight. Looking at{" "}
        <FilterPill value={lensId} options={lensOptions} onChange={setLensId} />, you have{" "}
        <span className="text-crit-400 font-semibold tabular-nums">
          {totals.high_count} high
        </span>
        ,{" "}
        <span className="text-warn-400 font-semibold tabular-nums">
          {totals.medium_count} medium
        </span>
        , and{" "}
        <span className="text-cyber-400 font-semibold tabular-nums">
          {totals.low_count} low
        </span>{" "}
        outstanding finding{totals.high_count + totals.medium_count + totals.low_count === 1 ? "" : "s"}. Your
        posture letter is{" "}
        <span className="text-glow-cyber text-cyber-400 font-mono text-2xl font-semibold align-baseline">
          {totals.score_letter}
        </span>{" "}
        <span className="text-chrome-400">
          ({totals.score_value}/100,{" "}
          <span className={trendColor}>
            {trendArrow} {Math.abs(totals.trend_delta)} over 30d
          </span>
          )
        </span>
        .
      </p>
    </article>
  );
}
